import { BearingEnvelope } from '@paragon/contract-library';
import { LineSegment2D, Point2D, Polygon2D } from '@paragon/geometry-library';
import { getEnvelopeSegment } from '@paragon/layout';

export type BearingEnvelopeHitTarget =
  | {
      readonly type: 'edge';
      readonly edge: LineSegment2D;
    }
  | {
      readonly type: 'interior';
    };

/**
 * Edge targets take priority over interior targets - dragging ends to resize envelopes is more important than dragging
 *   the whole envelope, which can be done from anywhere.
 */
export function classifyBearingEnvelopeHitTarget(
  layoutPosition: Point2D,
  shape: Polygon2D,
  envelope: BearingEnvelope,
  threshold: number,
): BearingEnvelopeHitTarget | null {
  const definingSegment = getEnvelopeSegment(envelope);
  const closestEndEdge = definingSegment ? findClosestEndEdge(layoutPosition, shape, definingSegment, threshold) : null;

  if (closestEndEdge) return { type: 'edge', edge: closestEndEdge };
  if (shape.containsPoint(layoutPosition)) return { type: 'interior' };

  return null;
}

/**
 * Finds the closest "end" edge (perpendicular to the defining line) to interact with for resizing envelopes.
 */
function findClosestEndEdge(
  layoutPosition: Point2D,
  shape: Polygon2D,
  definingSegment: LineSegment2D,
  threshold: number,
): LineSegment2D | null {
  let closest: { edge: LineSegment2D; distance: number } | null = null;

  for (const edge of shape.edges) {
    if (edge.isParallelTo(definingSegment)) continue;

    const distance = layoutPosition.distanceToLineSegment(edge);
    if (distance > threshold) continue;

    if (!closest || distance < closest.distance) {
      closest = { edge, distance };
    }
  }

  return closest?.edge ?? null;
}
